<template>
	<div class="opportunity-edit-fields" :class="{ inline: section }">
		<section v-if="!section || section === 'assetCategory'" class="field-group">
			<h4 v-if="!section">{{ t("opportunities.accordions.assetCategory") }}</h4>
			<EditableField
				type="select"
				:title="t('opportunities.assetType')"
				name="assetType"
				:options="assetTypeOptions"
				:multiple="true"
				:value="assetType"
				:disabled="disabled || isOpportunityLoading"
				@input="handleAssetTypeInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.category')"
				name="landCategoryNew"
				:options="landCategoryOptions"
				:multiple="true"
				:value="landCategory"
				:disabled="disabled || isOpportunityLoading"
				@input="handleLandCategoryInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.subcategory')"
				name="landSubcategory"
				:options="landSubcategoryOptions"
				:multiple="true"
				:value="landSubcategory"
				:disabled="disabled || isOpportunityLoading"
				@input="handleLandSubcategoryInput"
			/>
		</section>

		<section v-if="!section || section === 'pricing'" class="field-group">
			<h4 v-if="!section">{{ t("opportunities.accordions.pricing") }}</h4>
			<EditableField
				type="select"
				:title="t('opportunities.pricingSource')"
				name="pricingSource"
				:options="pricingSourceOptions"
				:value="pricingSource"
				:disabled="disabled || isOpportunityLoading"
				@input="handlePricingSourceInput"
			/>
			<EditableField
				type="number"
				:rounding="1"
				:title="unitFieldLabel(OpportunityUnitField.BuildableArea)"
				name="buildableArea"
				:value="buildableArea"
				:disabled="disabled || isOpportunityLoading"
				@input="handleBuildableAreaInput"
			/>
			<EditableField
				type="number"
				:title="t('opportunities.residentialUnits')"
				name="residentialUnits"
				:value="residentialUnits"
				:disabled="disabled || isOpportunityLoading"
				@input="handleResidentialUnitsInput"
			/>
		</section>

		<section
			v-if="(!section || section === 'ownerInformation') && opportunity.type !== OpportunityType.ListingLanderz"
			class="field-group"
		>
			<h4 v-if="!section">{{ t("opportunities.accordions.ownerInformation") }}</h4>
			<EditableField
				type="select"
				:title="t('opportunities.ownerTypes')"
				name="ownerTypes"
				:options="ownerTypeOptions"
				:multiple="true"
				:value="ownerTypes"
				:disabled="disabled || isOpportunityLoading"
				@input="handleOwnerTypesInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.ownershipDuration')"
				name="ownershipDuration"
				:options="ownershipDurationOptions"
				:value="ownershipDuration"
				:disabled="disabled || isOpportunityLoading"
				@input="handleOwnershipDurationInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.motivationsToSell')"
				name="motivationsToSell"
				:options="motivationToSellOptions"
				:multiple="true"
				:value="motivationsToSell"
				:disabled="disabled || isOpportunityLoading"
				@input="handleMotivationsToSellInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.potentialDealStructures')"
				name="potentialDealStructures"
				:options="potentialDealStructureOptions"
				:multiple="true"
				:value="potentialDealStructures"
				:disabled="disabled || isOpportunityLoading"
				@input="handlePotentialDealStructuresInput"
			/>
			<EditableField
				class="wide-field"
				type="textarea"
				:title="t('opportunities.commentsAboutOwner')"
				name="commentsAboutOwner"
				:value="commentsAboutOwner"
				:disabled="disabled || isOpportunityLoading"
				@input="handleCommentsAboutOwnerInput"
			/>
		</section>

		<section v-if="!section || section === 'potentialProject'" class="field-group">
			<h4 v-if="!section">{{ t("opportunities.accordions.potentialProject") }}</h4>
			<EditableField
				type="select"
				:title="t('opportunities.mainZoning')"
				name="mainZoning"
				:options="mainZoningOptions"
				:value="mainZoning"
				:disabled="disabled || isOpportunityLoading"
				@input="handleMainZoningInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.projectApprovalProcesses')"
				name="projectApprovalProcesses"
				:options="projectApprovalProcessOptions"
				:multiple="true"
				:value="projectApprovalProcesses"
				:disabled="disabled || isOpportunityLoading"
				@input="handleProjectApprovalProcessesInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.municipalReceptiveness')"
				name="municipalReceptiveness"
				:options="municipalReceptivenessOptions"
				:multiple="true"
				:value="municipalReceptiveness"
				:disabled="disabled || isOpportunityLoading"
				@input="handleMunicipalReceptivenessInput"
			/>
			<EditableField
				type="url"
				:title="t('opportunities.modelling_link')"
				name="modellingLink"
				:value="modellingLink"
				:disabled="disabled || isOpportunityLoading"
				@input="handleModellingLinkInput"
			/>
			<EditableField
				type="date"
				:title="t('opportunities.lastConversationCity')"
				name="lastConversationCity"
				:value="lastConversationCity"
				:disabled="disabled || isOpportunityLoading"
				@input="handleLastConversationCityInput"
			/>
			<EditableField
				class="wide-field"
				type="textarea"
				:title="t('opportunities.information_on_potential_and_zoning')"
				name="potentialProjectComments"
				:value="potentialProjectComments"
				:disabled="disabled || isOpportunityLoading"
				@input="handlePotentialProjectCommentsInput"
			/>
		</section>

		<section v-if="!section || section === 'existingConditions'" class="field-group">
			<h4 v-if="!section">{{ t("opportunities.accordions.existingConditions") }}</h4>
			<EditableField
				type="select"
				:title="t('opportunities.knownDevelopmentRestrictions')"
				name="knownDevelopmentRestrictions"
				:options="knownDevelopmentRestrictionOptions"
				:multiple="true"
				:value="knownDevelopmentRestrictions"
				:disabled="disabled || isOpportunityLoading"
				@input="handleKnownDevelopmentRestrictionsInput"
			/>
			<EditableField
				type="select"
				:title="t('opportunities.municipalInfrastructures')"
				name="municipalInfrastructures"
				:options="municipalInfrastructureOptions"
				:multiple="true"
				:value="municipalInfrastructures"
				:disabled="disabled || isOpportunityLoading"
				@input="handleMunicipalInfrastructuresInput"
			/>
			<EditableField
				class="wide-field"
				type="textarea"
				:title="t('opportunities.information_on_existing_conditions')"
				name="existingConditionsComments"
				:value="existingConditionsComments"
				:disabled="disabled || isOpportunityLoading"
				@input="handleExistingConditionsCommentsInput"
			/>
		</section>
	</div>
</template>

<script setup lang="ts">
import EditableField from "@/components/transactions/EditableField.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AssetType, LandCategory, LandSubcategory, OpportunityType, type UpdateOpportunityInput } from "@/graphql"
import {
	KnownDevelopmentRestriction,
	MainZoning,
	MotivationToSell,
	MunicipalInfrastructure,
	MunicipalReceptiveness,
	type Opportunity,
	OwnerType,
	OwnershipDuration,
	PotentialDealStructure,
	PricingSource,
	ProjectApprovalProcess,
} from "@/model/DataModel"
import { UseRootStore } from "@/model/RootStore"
import { useToastStore } from "@/stores/toastStore"
import { OpportunityUnitField, opportunityUnitFieldLabel } from "@/utils/opportunityUnitFields"
import { AreaUnit } from "@/utils/unitSystem"
import { ref } from "vue"
import { useI18n } from "vue-i18n"

const props = defineProps<{
	opportunity: Opportunity
	section?: string
	disabled?: boolean
}>()

const emit = defineEmits<{
	(e: "saving", value: boolean): void
}>()

const { t } = useI18n()
const store = UseRootStore()
const toastStore = useToastStore()
const { areaUnitSymbol, pricePerAreaUnitSymbol, convertAreaToPreferredUnit, convertAreaFromPreferredUnit } =
	useUnitSystem()
const isOpportunityLoading = ref(false)
const assetType = ref(props.opportunity.assetType ?? [])
const landCategory = ref(props.opportunity.landCategoryNew ?? [])
const landSubcategory = ref(props.opportunity.landSubcategory ?? [])
const pricingSource = ref(props.opportunity.pricingSource)
const ownerTypes = ref(props.opportunity.ownerTypes ?? [])
const ownershipDuration = ref(props.opportunity.ownershipDuration)
const motivationsToSell = ref(props.opportunity.motivationsToSell ?? [])
const potentialDealStructures = ref(props.opportunity.potentialDealStructures ?? [])
const mainZoning = ref(props.opportunity.mainZoning)
const projectApprovalProcesses = ref(props.opportunity.projectApprovalProcesses ?? [])
const municipalReceptiveness = ref(props.opportunity.municipalReceptiveness ?? [])
const knownDevelopmentRestrictions = ref(props.opportunity.knownDevelopmentRestrictions ?? [])
const municipalInfrastructures = ref(props.opportunity.municipalInfrastructures ?? [])
const lastConversationCity = ref(formatDate(props.opportunity.lastConversationCity))
const buildableArea = ref(
	props.opportunity.constructible == null
		? null
		: convertAreaToPreferredUnit(props.opportunity.constructible, AreaUnit.SquareFeet)
)
const residentialUnits = ref(props.opportunity.residentialUnits)
const commentsAboutOwner = ref(props.opportunity.commentsAboutOwner)
const modellingLink = ref<string | null>(props.opportunity.modelling_link ?? null)
const potentialProjectComments = ref<string | null>(props.opportunity.information_on_potential_and_zoning ?? null)
const existingConditionsComments = ref<string | null>(props.opportunity.information_on_existing_conditions ?? null)

const assetTypeOptions = Object.values(AssetType).map(value => ({
	value,
	label: t(`property.assetType.${value}`),
}))

const landCategoryOptions = Object.values(LandCategory).map(value => ({
	value,
	label: t(`property.landCategory.${value}`),
}))

const landSubcategoryOptions = Object.entries(LandSubcategory)
	.filter(([, value]) => value !== LandSubcategory.MidRise)
	.map(([key, value]) => ({
		value,
		label: t(`property.landSubcategory.${key}`),
	}))

const pricingSourceOptions = Object.entries(PricingSource).map(([key, value]) => ({
	value,
	label: t(`property.pricingSource.${key}`),
}))

const ownerTypeOptions = Object.entries(OwnerType).map(([key, value]) => ({
	value,
	label: t(`property.ownerType.${key}`),
}))

const ownershipDurationOptions = Object.entries(OwnershipDuration).map(([key, value]) => ({
	value,
	label: t(`property.ownershipDuration.${key}`),
}))

const motivationToSellOptions = Object.entries(MotivationToSell).map(([key, value]) => ({
	value,
	label: t(`property.motivationToSell.${key}`),
}))

const potentialDealStructureOptions = Object.entries(PotentialDealStructure).map(([key, value]) => ({
	value,
	label: t(`property.potentialDealStructure.${key}`),
}))

const mainZoningOptions = Object.entries(MainZoning).map(([key, value]) => ({
	value,
	label: t(`property.mainZoning.${key}`),
}))

const projectApprovalProcessOptions = Object.entries(ProjectApprovalProcess).map(([key, value]) => ({
	value,
	label: t(`property.projectApprovalProcess.${key}`),
}))

const municipalReceptivenessOptions = Object.entries(MunicipalReceptiveness).map(([key, value]) => ({
	value,
	label: t(`property.municipalReceptiveness.${key}`),
}))

const knownDevelopmentRestrictionOptions = Object.entries(KnownDevelopmentRestriction).map(([key, value]) => ({
	value,
	label: t(`property.knownDevelopmentRestriction.${key}`),
}))

const municipalInfrastructureOptions = Object.entries(MunicipalInfrastructure).map(([key, value]) => ({
	value,
	label: t(`property.municipalInfrastructure.${key}`),
}))

async function syncOpportunity<Field extends keyof UpdateOpportunityInput>(
	fieldName: Field,
	value: UpdateOpportunityInput[Field],
	rollback?: () => void
): Promise<void> {
	isOpportunityLoading.value = true
	emit("saving", true)
	const input = { [fieldName]: value }

	try {
		await store.updateOpportunity(
			{
				opportunityId: props.opportunity.id,
				...input,
			},
			props.opportunity
		)
		const modelFields: Partial<Record<keyof UpdateOpportunityInput, keyof Opportunity>> = {
			buildableArea: "constructible",
			modellingLink: "modelling_link",
			potentialProjectComments: "information_on_potential_and_zoning",
			existingConditionsComments: "information_on_existing_conditions",
		}
		const modelFieldName = modelFields[fieldName] ?? fieldName
		const modelValue =
			fieldName === "lastConversationCity" && typeof value === "string" ? new Date(`${value}T00:00:00.000Z`) : value
		Object.assign(props.opportunity, { [modelFieldName]: modelValue })
	} catch (error) {
		rollback?.()
		console.error(`Failed to update opportunity field "${fieldName}":`, error)
		toastStore.error("opportunities-table.edit-modal.sync-error")
	} finally {
		isOpportunityLoading.value = false
		emit("saving", false)
	}
}

async function handleAssetTypeInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = assetType.value
	const nextValue = Array.isArray(value)
		? value.filter((item): item is AssetType => Object.values(AssetType).includes(item as AssetType))
		: []
	assetType.value = nextValue

	await syncOpportunity("assetType", nextValue, () => {
		assetType.value = previousValue
	})
}

async function handleLandCategoryInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = landCategory.value
	const nextValue = Array.isArray(value)
		? value.filter((item): item is LandCategory => Object.values(LandCategory).includes(item as LandCategory))
		: []
	landCategory.value = nextValue

	await syncOpportunity("landCategoryNew", nextValue, () => {
		landCategory.value = previousValue
	})
}

async function handleLandSubcategoryInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = landSubcategory.value
	const nextValue = Array.isArray(value)
		? value.filter((item): item is LandSubcategory => Object.values(LandSubcategory).includes(item as LandSubcategory))
		: []
	landSubcategory.value = nextValue

	await syncOpportunity("landSubcategory", nextValue, () => {
		landSubcategory.value = previousValue
	})
}

async function handlePricingSourceInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = pricingSource.value
	pricingSource.value = Object.values(PricingSource).includes(value as PricingSource) ? (value as PricingSource) : null

	await syncOpportunity("pricingSource", pricingSource.value, () => {
		pricingSource.value = previousValue
	})
}

async function handleBuildableAreaInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = buildableArea.value
	buildableArea.value = typeof value === "number" ? value : null
	const areaInSquareFeet =
		buildableArea.value == null
			? null
			: Math.round(convertAreaFromPreferredUnit(buildableArea.value, AreaUnit.SquareFeet))

	await syncOpportunity("buildableArea", areaInSquareFeet, () => {
		buildableArea.value = previousValue
	})
}

async function handleResidentialUnitsInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = residentialUnits.value
	residentialUnits.value = typeof value === "number" ? value : null

	await syncOpportunity("residentialUnits", residentialUnits.value, () => {
		residentialUnits.value = previousValue
	})
}

async function handleOwnerTypesInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = ownerTypes.value
	ownerTypes.value = Array.isArray(value)
		? value.filter((item): item is OwnerType => Object.values(OwnerType).includes(item as OwnerType))
		: []

	await syncOpportunity("ownerTypes", ownerTypes.value, () => {
		ownerTypes.value = previousValue
	})
}

async function handleOwnershipDurationInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = ownershipDuration.value
	ownershipDuration.value = Object.values(OwnershipDuration).includes(value as OwnershipDuration)
		? (value as OwnershipDuration)
		: null

	await syncOpportunity("ownershipDuration", ownershipDuration.value, () => {
		ownershipDuration.value = previousValue
	})
}

async function handleMotivationsToSellInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = motivationsToSell.value
	motivationsToSell.value = Array.isArray(value)
		? value.filter((item): item is MotivationToSell =>
				Object.values(MotivationToSell).includes(item as MotivationToSell)
			)
		: []

	await syncOpportunity("motivationsToSell", motivationsToSell.value, () => {
		motivationsToSell.value = previousValue
	})
}

async function handlePotentialDealStructuresInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = potentialDealStructures.value
	potentialDealStructures.value = Array.isArray(value)
		? value.filter((item): item is PotentialDealStructure =>
				Object.values(PotentialDealStructure).includes(item as PotentialDealStructure)
			)
		: []

	await syncOpportunity("potentialDealStructures", potentialDealStructures.value, () => {
		potentialDealStructures.value = previousValue
	})
}

async function handleMainZoningInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = mainZoning.value
	mainZoning.value = Object.values(MainZoning).includes(value as MainZoning) ? (value as MainZoning) : null

	await syncOpportunity("mainZoning", mainZoning.value, () => {
		mainZoning.value = previousValue
	})
}

async function handleProjectApprovalProcessesInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = projectApprovalProcesses.value
	projectApprovalProcesses.value = Array.isArray(value)
		? value.filter((item): item is ProjectApprovalProcess =>
				Object.values(ProjectApprovalProcess).includes(item as ProjectApprovalProcess)
			)
		: []

	await syncOpportunity("projectApprovalProcesses", projectApprovalProcesses.value, () => {
		projectApprovalProcesses.value = previousValue
	})
}

async function handleMunicipalReceptivenessInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = municipalReceptiveness.value
	municipalReceptiveness.value = Array.isArray(value)
		? value.filter((item): item is MunicipalReceptiveness =>
				Object.values(MunicipalReceptiveness).includes(item as MunicipalReceptiveness)
			)
		: []

	await syncOpportunity("municipalReceptiveness", municipalReceptiveness.value, () => {
		municipalReceptiveness.value = previousValue
	})
}

async function handleKnownDevelopmentRestrictionsInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = knownDevelopmentRestrictions.value
	knownDevelopmentRestrictions.value = Array.isArray(value)
		? value.filter((item): item is KnownDevelopmentRestriction =>
				Object.values(KnownDevelopmentRestriction).includes(item as KnownDevelopmentRestriction)
			)
		: []

	await syncOpportunity("knownDevelopmentRestrictions", knownDevelopmentRestrictions.value, () => {
		knownDevelopmentRestrictions.value = previousValue
	})
}

async function handleMunicipalInfrastructuresInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = municipalInfrastructures.value
	municipalInfrastructures.value = Array.isArray(value)
		? value.filter((item): item is MunicipalInfrastructure =>
				Object.values(MunicipalInfrastructure).includes(item as MunicipalInfrastructure)
			)
		: []

	await syncOpportunity("municipalInfrastructures", municipalInfrastructures.value, () => {
		municipalInfrastructures.value = previousValue
	})
}

async function handleLastConversationCityInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = lastConversationCity.value
	lastConversationCity.value = typeof value === "string" ? value : null

	await syncOpportunity("lastConversationCity", lastConversationCity.value, () => {
		lastConversationCity.value = previousValue
	})
}

async function handleCommentsAboutOwnerInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = commentsAboutOwner.value
	commentsAboutOwner.value = typeof value === "string" ? value : null

	await syncOpportunity("commentsAboutOwner", commentsAboutOwner.value, () => {
		commentsAboutOwner.value = previousValue
	})
}

async function handleModellingLinkInput({ value }: { value: unknown }): Promise<void> {
	modellingLink.value = typeof value === "string" ? value : null

	if (modellingLink.value) {
		try {
			const link = new URL(modellingLink.value)

			if (link.protocol !== "http:" && link.protocol !== "https:") {
				return
			}
		} catch {
			return
		}
	}

	await syncOpportunity("modellingLink", modellingLink.value)
}

async function handlePotentialProjectCommentsInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = potentialProjectComments.value
	potentialProjectComments.value = typeof value === "string" ? value : null

	await syncOpportunity("potentialProjectComments", potentialProjectComments.value, () => {
		potentialProjectComments.value = previousValue
	})
}

async function handleExistingConditionsCommentsInput({ value }: { value: unknown }): Promise<void> {
	const previousValue = existingConditionsComments.value
	existingConditionsComments.value = typeof value === "string" ? value : null

	await syncOpportunity("existingConditionsComments", existingConditionsComments.value, () => {
		existingConditionsComments.value = previousValue
	})
}

function unitFieldLabel(field: OpportunityUnitField): string {
	return opportunityUnitFieldLabel(field, t, areaUnitSymbol.value, pricePerAreaUnitSymbol.value)
}

function formatDate(value: Date | null | undefined): string | null {
	if (value == null || Number.isNaN(value.getTime())) {
		return null
	}

	return value.toISOString().slice(0, 10)
}
</script>

<style scoped>
.opportunity-edit-fields {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
}

.field-group {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
	align-items: flex-start;
	gap: var(--spacing-lg) var(--spacing-xl);
	padding-bottom: var(--spacing-lg);
	border-bottom: var(--border-width-thin) solid var(--neutral-100);

	&:last-child {
		padding-bottom: 0;
		border-bottom: none;
	}

	h4 {
		grid-column: 1 / -1;
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.wide-field {
		grid-column: 1 / -1;
	}
}

.field-group :deep(.editable-field) {
	flex-flow: row wrap;
	align-items: center;
	gap: var(--space-4) var(--spacing-xs);
	padding: 0 0 var(--spacing-xs);
	border-bottom: none;
}

.field-group :deep(.editable-field .title) {
	flex: 1 0 100%;
	align-self: flex-start;
}

.field-group :deep(.editable-field .value) {
	justify-content: flex-start;
	align-self: center;
	flex: 1 1 auto;
	min-width: 0;
}

.field-group :deep(.editable-field .value-text) {
	width: 100%;
	min-height: var(--spacing-xl);
	display: flex;
	align-items: center;
	padding: var(--spacing-xs);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	background-color: var(--neutral-50);
	box-sizing: border-box;
	text-align: left;
}

.inline .field-group {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	width: 100%;
}

.inline .field-group :deep(.editable-field) {
	width: 100%;
	flex-wrap: nowrap;
}

.inline .field-group :deep(.editable-field .title) {
	flex: 0 0 var(--flex-basis-field);
	align-self: center;
}

.inline .field-group :deep(.editable-field .value) {
	flex: 1;
	justify-content: flex-end;
}
</style>
